import { Grid, IconButton, TextField, Typography } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { PushPin, PushPinOutlined } from '@mui/icons-material'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'

/**
* @typedef {import("../generic/utilities/objectStructures/planner/event").PinnedSettings} PinnedSettings PinnedSettings object
* @typedef {import("../generic/utilities/objectStructures/planner/event").Job} Job Job object
**/

/**
 * "Pin Setting" tab of the planner event dialog.
 *
 * Story 96 (CA10/RN37/P6): holds the plan date and its date pin, and nothing else. The machine
 * selector and the per-task machine pin used to live here too; the selector moved to the "Job
 * Settings" tab -- the single place where a machine is fixed -- and the machine pin is retired
 * altogether (RN27/INV6). The AI Planner renders this very component, so it lost the same pin and
 * nothing more (RN40/D4).
 *
 * @param {object} Props
 * @param {PinnedSettings} Props.pinSettings PinnedSettings object
 * @param {Job} Props.newJobPinSettings new Job object
 * @param {function(Job)} Props.onChangeNewPinJobSettings Function to handle new pin Job settings change
 * @returns
 */
const PinJobEventDialog = ({
  pinSettings,
  newJobPinSettings,
  onChangeNewPinJobSettings
}) => {
  const { t_ } = useSmartTranslation()
  const { format, ampm, formatDateTime, parseDateTime } = useDateFormatter()

  if (!pinSettings || !newJobPinSettings) {
    return null
  }

  return (

    <Grid container spacing={2} direction='row'>
      <Grid container xs={12} direction='row' style={{ marginTop: '2rem', alignItems: 'center', textAlign: 'left' }} spacing={2}>
        {
          pinSettings.previousJob
            ? (
              <>
                <Grid item xs={3}>
                  <Typography>{t_('Previous Job') + ': ' + pinSettings.previousJob?.jobName}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={t_('End date')}
                    value={formatDateTime(pinSettings.previousJob?.dateEnd)}
                    disabled
                  />
                </Grid>
              </>
              )
            : null
        }
      </Grid>
      <Grid container xs={12} direction='row' style={{ marginTop: '2rem', alignItems: 'center', textAlign: 'left' }} spacing={2}>
        <Grid item xs={3}>
          <Typography color='primary'>{t_('Current Job') + ': ' + pinSettings.currentJob?.jobName}</Typography>
        </Grid>
        <Grid item xs={4}>
          <DateTimePicker
            margin='normal'
            ampm={ampm}
            label={t_('Start date')}
            format={format.dateTime}
            value={parseDateTime(formatDateTime(newJobPinSettings?.dateStart))}
            onChange={(newValue) => { onChangeNewPinJobSettings({ ...newJobPinSettings, dateStart: newValue }) }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton
            style={{ alignSelf: 'end', width: 'fit-content' }}
            onClick={() => {
              onChangeNewPinJobSettings({ ...newJobPinSettings, pinnedDate: !newJobPinSettings.pinnedDate })
            }}
          >
            {newJobPinSettings?.pinnedDate ? <PushPin /> : <PushPinOutlined />}
          </IconButton>
        </Grid>
      </Grid>
      <Grid container direction='row' xs={12} style={{ marginTop: '2rem', alignItems: 'center', textAlign: 'left' }} spacing={2}>
        {
          pinSettings?.nextJob
            ? (
              <>
                <Grid item xs={3}>
                  <Typography>{t_('Next Job') + ': ' + pinSettings.nextJob?.jobName}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={t_('Start date')}
                    value={formatDateTime(pinSettings.nextJob?.dateStart)}
                    disabled
                  />
                </Grid>
              </>
              )
            : null
        }
      </Grid>
    </Grid>
  )
}

export default PinJobEventDialog
